import { Grid, Tooltip } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import Helmet from "react-helmet";

import PageviewIcon from "@mui/icons-material/Pageview";
import {
    Box,
    Card,
    CardContent,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { PagedTable, pageStore } from "../../../common";
import { Portlet, PortletBody } from "../../../partials/content/Portlet";
import {
    promptManagementLanding,
    resetPage,
    set,
    setMulti,
} from "../../../store/actions/pages";
import { RootState } from "../../../store/reducers";
import {
    createIconDataField,
    createStringDataField,
} from "../../../util/format";
import PromptModal from "./PromptModal";
import { useData } from "@pure-ptr/react";
import { getPromptCategories, getPromptVersions } from "../../../store/api/promptManagement";

const { usePageStore, useGridFilters } = pageStore(
    "prompt-management-landing",
    {}
);

export default function PromptManagementPage() {
    const [refresh, setRefresh] = useState(Promise.resolve());
    const filters: any = useSelector<RootState>(
        (state) => state?.pages?.["prompt-management-landing"]
    );
    const appliedFilters = filters?.filter;
    const [category, setCategory] = useState("");
    const [version, setVersion] = useState("");
    const [showOnlyActive, setShowOnlyActive] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const dispatch = useDispatch();

    const promptData = usePageStore((s) => {
        return s.list;
    });

    const pending = usePageStore((s) => s.pending);

    // Column fields
    const fields = useMemo(
        () => [
            createStringDataField("key", "Key", { sortable: true }),
            createStringDataField("category", "Category", { sortable: true }),
            createStringDataField("description", "Description", {
                sortable: false,
            }),
            createStringDataField("promptVersion", "Prompt Version", {
                sortable: true,
            }),
            createIconDataField("isActive", "Active", {
                hideFromFilter: true,
                icon: "fas fa-check-circle",
                sortable: true,
            }),
        ],
        []
    );

    const { data: categories, isPending: isLoadingCategories } = useData(async () => {
        const response = await getPromptCategories();
        return response;
    });

    const { data: versions, isPending: isLoadingVersions } = useData(async () => {
        const response = await getPromptVersions();
        return response;
    });

    const tableFilter = useGridFilters();

    function handleRefresh() {
        dispatch(set("prompt-management-landing", "list", []));
        setRefresh(Promise.resolve());
    }
    useEffect(() => {
        const pageName = { name: "prompt-management-landing" };
        dispatch(set(pageName.name, "waitForCriteriaFilter", false));
        return () => {
            dispatch(resetPage(pageName.name));
        };
    }, [dispatch]);
    const actionComponent = (row) => {
        return (
            <Tooltip title="View Prompt">
              <IconButton
                size="small"
                onClick={() => {
                    setSelectedPrompt(row);
                    setModalOpen(true);
                }}
                color="primary"
            >
                <PageviewIcon />
            </IconButton>
            </Tooltip>
        );
    };

    // Sync local filter states with Redux filters
    useEffect(() => {
    if (appliedFilters?.length) {
        const cat = appliedFilters.find(f => f.id === "category")?.value || "all";
        const ver = appliedFilters.find(f => f.id === "promptVersion")?.value || "all";
        const active = appliedFilters.find(f => f.id === "isActive")?.value || false;
        setCategory(cat);
        setVersion(ver);
        setShowOnlyActive(active);
    } else {
        // No filters — reset UI
        setCategory("all");
        setVersion("all");
        setShowOnlyActive(false);
    }

    }, [appliedFilters]);

    useEffect(() => {
        return () => {
            dispatch(setMulti(
                "prompt-management-landing",
                {
                    filter: [],
                }
            ));
        };
    }, []);
    return (
        <>
            <Helmet title="Prompt Management" />
            <Portlet>
                <PortletBody>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={2}
                                flexWrap="wrap"
                                justifyContent="end"
                                mb={2}
                            >
                                <Box display="flex" gap={2} flexWrap="wrap">
                                    {/* Filter by Category */}
                                    <FormControl
                                        size="small"
                                        sx={{ minWidth: 200 }}
                                    >
                                        <InputLabel>
                                            Filter by Category
                                        </InputLabel>
                                        <Select
                                            label="Filter by Category"
                                            value={category}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setCategory(value);

                                                // build updated filters
                                                const updatedFilters =
                                                    appliedFilters.filter(
                                                        (item) =>
                                                            item.id !==
                                                            "category"
                                                    );

                                                if (value && value !== "all") {
                                                    updatedFilters.push({
                                                        id: "category",
                                                        equator: "equals",
                                                        value,
                                                    });
                                                }

                                                dispatch(
                                                    setMulti(
                                                        "prompt-management-landing",
                                                        {
                                                            filter: updatedFilters,
                                                        }
                                                    )
                                                );
                                                handleRefresh();
                                            }}
                                            disabled={
                                                !!(isLoadingCategories || !categories?.length)
                                            }
                                        >
                                            <MenuItem value="all">All</MenuItem>
                                            {categories?.map((cat, index) => (
                                                <MenuItem
                                                    key={index}
                                                    value={cat.category}
                                                >
                                                    {cat.category}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* Filter by Prompt Version */}
                                    <FormControl
                                        size="small"
                                        sx={{ minWidth: 200 }}
                                    >
                                        <InputLabel>
                                            Filter by Prompt Version
                                        </InputLabel>
                                        <Select
                                            label="Filter by Prompt Version"
                                            value={version}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setVersion(value);

                                                const updatedFilters =
                                                    appliedFilters.filter(
                                                        (item) =>
                                                            item.id !==
                                                            "promptVersion"
                                                    );

                                                if (value && value !== "all") {
                                                    updatedFilters.push({
                                                        id: "promptVersion",
                                                        equator: "equals",
                                                        value,
                                                    });
                                                }

                                                dispatch(
                                                    setMulti(
                                                        "prompt-management-landing",
                                                        {
                                                            filter: updatedFilters,
                                                        }
                                                    )
                                                );
                                                handleRefresh();
                                            }}
                                            disabled={
                                                !!(isLoadingVersions || !versions?.length)
                                            }
                                        >
                                            <MenuItem value="all">All</MenuItem>
                                            {
                                              versions?.length > 0 && <MenuItem
                                                value={
                                                  [...versions]
                                                    .sort((a, b) => parseFloat(b.version) - parseFloat(a.version))[0]?.version
                                                }
                                              >Latest Version</MenuItem>
                                            }
                                            {versions?.map((ver,index) => (
                                                <MenuItem key={index} value={ver?.version}>
                                                    {ver?.version}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* Show Only Active */}
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <Typography>
                                            Show Only Active
                                        </Typography>
                                        <Switch
                                            checked={showOnlyActive}
                                            onChange={() => {
                                                const newValue =
                                                    !showOnlyActive;
                                                setShowOnlyActive(newValue);

                                                const updatedFilters =
                                                    appliedFilters.filter(
                                                        (item) =>
                                                            item.id !==
                                                            "isActive"
                                                    );

                                                if (newValue) {
                                                    updatedFilters.push({
                                                        id: "isActive",
                                                        equator: "equals",
                                                        value: true,
                                                    });
                                                }

                                                dispatch(
                                                    setMulti(
                                                        "prompt-management-landing",
                                                        {
                                                            filter: updatedFilters,
                                                        }
                                                    )
                                                );
                                                handleRefresh();
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Card>
                                <CardContent>
                                    <PagedTable
                                        name="prompt-management-landing"
                                        refresh={refresh}
                                        loading={pending}
                                        data={promptData}
                                        fields={fields}
                                        hasActions={true}
                                        renderActionColumn={actionComponent}
                                        showFilterColumnsDialog={false}
                                        paginationFilters={tableFilter}
                                        defaultSort={[
                                            { id: "id", direction: "desc" },
                                        ]}
                                        onLoad={(sort, filter, pagination) => {
                                            dispatch(
                                                promptManagementLanding(
                                                    pagination,
                                                    sort,
                                                    filter
                                                )
                                            );
                                        }}
                                        filterData={appliedFilters}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <PromptModal
                        open={modalOpen}
                        close={() => setModalOpen(false)}
                        prompt={selectedPrompt}
                        onClose={() => setSelectedPrompt(null)}
                    />
                </PortletBody>
            </Portlet>
        </>
    );
}
